import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Loader2, Lock, AlertCircle, LogOut, Clock, AlertTriangle } from 'lucide-react';
import { useValidateAdminPasscode, useGetTemporaryAccessStatus, useClearTemporaryAdminSession, useIsCallerTemporaryAdmin } from '../hooks/useQueries';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface TemporaryAdminAccessModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function TemporaryAdminAccessModal({ open, onOpenChange }: TemporaryAdminAccessModalProps) {
    const [passcode, setPasscode] = useState('');
    const [error, setError] = useState('');
    const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number | null>(null);
    const [showWarning, setShowWarning] = useState(false);
    const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
    
    const { mutate: validatePasscode, isPending } = useValidateAdminPasscode();
    const { mutate: clearSession, isPending: isClearing } = useClearTemporaryAdminSession();
    const { data: temporaryAccessEnabled } = useGetTemporaryAccessStatus();
    const { data: isTemporaryAdmin } = useIsCallerTemporaryAdmin();
    const queryClient = useQueryClient();

    // Update activity timestamp on user interaction
    useEffect(() => {
        if (!isTemporaryAdmin) return;

        const updateActivity = () => {
            setLastActivityTime(Date.now());
        };

        // Listen for user interactions
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
        };
    }, [isTemporaryAdmin]);

    // Session timer effect with inactivity-based timeout and 2-minute warning
    useEffect(() => {
        if (!isTemporaryAdmin) {
            setSessionTimeRemaining(null);
            setShowWarning(false);
            return;
        }

        const sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds

        const updateTimer = () => {
            const now = Date.now();
            const elapsed = now - lastActivityTime;
            const remaining = sessionTimeout - elapsed;

            if (remaining <= 0) {
                // Session expired due to inactivity
                setSessionTimeRemaining(0);
                setShowWarning(false);
                // Clear session from localStorage
                localStorage.removeItem('tempAdminSessionStart');
                // Invalidate queries to hide admin UI
                queryClient.invalidateQueries({ queryKey: ['isCallerTemporaryAdmin'] });
                queryClient.invalidateQueries({ queryKey: ['isCallerAdmin'] });
                // Don't show error toast - session expiration is expected behavior
                // Only log to console
                console.log('Admin session expired due to inactivity');
            } else {
                const remainingMinutes = Math.floor(remaining / 1000 / 60);
                setSessionTimeRemaining(remainingMinutes);
                
                // Show warning when 2 minutes or less remain
                if (remainingMinutes <= 2 && !showWarning) {
                    setShowWarning(true);
                    toast.warning('Admin session expiring soon', {
                        description: `Your session will expire in ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} of inactivity`,
                    });
                }
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 30000); // Update every 30 seconds (reduced frequency to lower CPU)

        return () => clearInterval(interval);
    }, [isTemporaryAdmin, lastActivityTime, queryClient, showWarning]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!passcode.trim()) {
            setError('Please enter a passcode');
            return;
        }

        validatePasscode(passcode, {
            onSuccess: async () => {
                try {
                    // Store session start time in localStorage
                    localStorage.setItem('tempAdminSessionStart', Date.now().toString());
                    setLastActivityTime(Date.now());
                    
                    toast.success('Admin access granted successfully');
                    
                    // Invalidate all admin-related queries to trigger UI refresh
                    try {
                        await queryClient.invalidateQueries({ queryKey: ['isCallerAdmin'] });
                        await queryClient.invalidateQueries({ queryKey: ['isCallerTemporaryAdmin'] });
                    } catch (invalidateError) {
                        console.error('Error invalidating queries:', invalidateError);
                    }
                    
                    // Force refetch to ensure immediate UI update - with error handling
                    try {
                        await queryClient.refetchQueries({ queryKey: ['isCallerAdmin'] });
                    } catch (refetchError) {
                        console.error('Error refetching isCallerAdmin:', refetchError);
                    }
                    
                    try {
                        // CRITICAL: Force refetch isCallerTemporaryAdmin immediately after validation
                        // First remove from cache to force fresh fetch
                        queryClient.removeQueries({ queryKey: ['isCallerTemporaryAdmin'] });
                        
                        // Wait a moment for cache to clear
                        await new Promise(resolve => setTimeout(resolve, 100));
                        
                        // Force refetch multiple times to ensure it updates
                        await queryClient.refetchQueries({ 
                            queryKey: ['isCallerTemporaryAdmin'],
                            type: 'active'
                        });
                        
                        // Additional refetch after short delay
                        setTimeout(async () => {
                            await queryClient.refetchQueries({ queryKey: ['isCallerTemporaryAdmin'] });
                        }, 300);
                        
                        // One more refetch after longer delay to catch any race conditions
                        setTimeout(async () => {
                            await queryClient.refetchQueries({ queryKey: ['isCallerTemporaryAdmin'] });
                        }, 1000);
                    } catch (refetchError) {
                        console.error('Error refetching isCallerTemporaryAdmin:', refetchError);
                    }
                    
                    setPasscode('');
                    onOpenChange(false);
                } catch (error) {
                    console.error('Error in onSuccess handler:', error);
                    // Don't show error toast - just log it
                }
            },
            onError: (err: any) => {
                // Extract a user-friendly error message (remove technical details)
                let errorMessage = 'Invalid passcode. Please try again.';
                if (err?.message) {
                    // Filter out technical error details that users don't need to see
                    const msg = err.message;
                    if (msg.includes('Unauthorized') || msg.includes('Anonymous')) {
                        errorMessage = 'Please authenticate with Internet Identity first.';
                    } else if (msg.includes('Invalid passcode') || msg.includes('Access Denied')) {
                        errorMessage = 'Invalid passcode. Please try again.';
                    } else if (msg.length < 100) {
                        // Only use short, user-friendly messages
                        errorMessage = msg;
                    }
                }
                setError(errorMessage);
                // Don't show toast - error is already displayed in the form
            },
        });
    };

    const handleDisconnect = () => {
        // Immediately clear admin status in cache
        queryClient.setQueryData(['isCallerAdmin'], false);
        queryClient.setQueryData(['isCallerTemporaryAdmin'], false);
        
        clearSession(undefined, {
            onSuccess: async () => {
                // Clear session from localStorage
                localStorage.removeItem('tempAdminSessionStart');
                
                toast.success('Admin session ended successfully');
                
                // Invalidate and refetch queries to ensure backend state is checked
                queryClient.setQueryData(['isCallerTemporaryAdmin'], false);
                await queryClient.invalidateQueries({ queryKey: ['isCallerAdmin'] });
                await queryClient.invalidateQueries({ queryKey: ['isCallerTemporaryAdmin'] });
                
                // Force immediate refetch
                await queryClient.refetchQueries({ queryKey: ['isCallerAdmin'] });
                await queryClient.refetchQueries({ queryKey: ['isCallerTemporaryAdmin'] });
                
                setSessionTimeRemaining(null);
                setShowWarning(false);
                onOpenChange(false);
            },
            onError: (err: any) => {
                const errorMessage = err?.message || 'Failed to end session';
                toast.error(errorMessage);
            },
        });
    };

    const handleClose = () => {
        setPasscode('');
        setError('');
        onOpenChange(false);
    };

    if (temporaryAccessEnabled === false) {
        return (
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock className="h-5 w-5 text-muted-foreground" />
                            Temporary Admin Access
                        </DialogTitle>
                        <DialogDescription>
                            Temporary admin access is currently disabled
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-3 p-4 bg-accent/30 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <p className="text-sm text-muted-foreground">
                            This feature has been disabled by the administrator. Please use wallet-based authentication instead.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleClose} variant="outline">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    // If already logged in as temporary admin, show session info and disconnect option
    if (isTemporaryAdmin) {
        return (
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock className="h-5 w-5 text-green-500" />
                            Temporary Admin Session Active
                        </DialogTitle>
                        <DialogDescription>
                            You are currently logged in as a temporary admin
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Session Timer Display */}
                        {sessionTimeRemaining !== null && (
                            <div className={`flex items-center gap-3 p-4 border rounded-lg ${
                                sessionTimeRemaining <= 2 
                                    ? 'bg-yellow-500/10 border-yellow-500/30' 
                                    : 'bg-green-500/10 border-green-500/30'
                            }`}>
                                <Clock className={`h-5 w-5 flex-shrink-0 ${
                                    sessionTimeRemaining <= 2 ? 'text-yellow-500' : 'text-green-500'
                                }`} />
                                <div className="flex-1">
                                    <p className={`text-sm font-medium ${
                                        sessionTimeRemaining <= 2 ? 'text-yellow-500' : 'text-green-500'
                                    }`}>
                                        Session expires in {sessionTimeRemaining} minute{sessionTimeRemaining !== 1 ? 's' : ''} of inactivity
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Timer resets with each interaction
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        {/* Warning Banner when 2 minutes or less remain */}
                        {sessionTimeRemaining !== null && sessionTimeRemaining <= 2 && (
                            <div className="flex items-center gap-3 p-4 bg-yellow-500/20 border border-yellow-500/40 rounded-lg">
                                <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-yellow-500">
                                        Session expiring soon!
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Please save any changes. Interact with the page to extend your session.
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        <Separator />
                        
                        <div className="bg-accent/30 rounded-lg p-4">
                            <p className="text-sm text-muted-foreground">
                                You have full admin access to the dashboard. Your session will automatically extend with each interaction. Click the button below to end your session and log out.
                            </p>
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isClearing}
                        >
                            Close
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleDisconnect}
                            disabled={isClearing}
                        >
                            {isClearing ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Disconnecting...
                                </>
                            ) : (
                                <>
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Disconnect Admin
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    // Login form (no Internet Identity required)
    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5 text-primary" />
                        Temporary Admin Access
                    </DialogTitle>
                    <DialogDescription>
                        Enter the admin passcode to access the dashboard
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="passcode">Admin Passcode</Label>
                        <Input
                            id="passcode"
                            type="password"
                            placeholder="Enter passcode"
                            value={passcode}
                            onChange={(e) => {
                                setPasscode(e.target.value);
                                setError('');
                            }}
                            disabled={isPending}
                            className={error ? 'border-destructive' : ''}
                        />
                        {error && (
                            <p className="text-sm text-destructive flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {error}
                            </p>
                        )}
                    </div>
                    <div className="bg-accent/30 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground">
                            Your session will remain active for 30 minutes of inactivity. The timer resets with each interaction.
                        </p>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending || !passcode.trim()}>
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Validating...
                                </>
                            ) : (
                                'Access Admin Dashboard'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
